/* V199 — Mika Ad Hero Gallery Admin UI
 * Shows the 6 generated Mika ad creatives (hi-res PNG hero images).
 * 1-click download → upload to Meta Ads Manager.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PATTERN_BADGES = {
  tripwire: { label: "💰 Tripwire €7", cls: "bg-yellow-600 text-black" },
  stop_banner: { label: "🛑 STOP banner", cls: "bg-amber-600 text-black" },
  timer_authority: { label: "⏱️ 90s Timer", cls: "bg-cyan-600 text-black" },
  big_money: { label: "💵 Big Money", cls: "bg-emerald-600 text-black" },
  coming_soon_mika: { label: "🚀 Coming Soon", cls: "bg-blue-600 text-white" },
  competitor_killer_mika: { label: "🎯 Killer", cls: "bg-rose-600 text-white" },
  before_after_metric_card: { label: "📊 Before/After (TryCrush-killer)", cls: "bg-fuchsia-600 text-white" },
  // V219 — Competitor Killer Pack (SK / CZ / EN × 3 patterns)
  pain_first: { label: "⚡ Pain-First", cls: "bg-emerald-500 text-black" },
  value_stack: { label: "💰 Value Stack", cls: "bg-yellow-400 text-black" },
  eu_local: { label: "🇪🇺 EU Local", cls: "bg-blue-600 text-white" },
  eu_sovereignty: { label: "🛡️ EU Sovereign", cls: "bg-indigo-600 text-yellow-300" },
  // V220 — RankWay Killer Pack (SK / CZ / EN × 3 concepts)
  mission_control: { label: "🛰️ Mission Control", cls: "bg-violet-600 text-white" },
  hammock_mode: { label: "🏝️ Hammock Mode", cls: "bg-amber-500 text-black" },
  four_verticals: { label: "🎯 4 Verticals", cls: "bg-rose-600 text-white" },
  // V223 — FOMO Funnel Pack (SK / CZ / EN × 4 concepts)
  competitor_envy: { label: "🔥 Competitor Envy", cls: "bg-red-600 text-white" },
  last_train: { label: "🚂 Last Train", cls: "bg-violet-700 text-yellow-300" },
  year_2026: { label: "📅 Year 2026", cls: "bg-cyan-500 text-black" },
  eu_ai_act_deadline: { label: "⚖️ EU AI Act D-47", cls: "bg-yellow-400 text-blue-900" },
};

export default function AdminMikaAdsTab() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genStatus, setGenStatus] = useState("");
  const [err, setErr] = useState("");
  const [expandedSlug, setExpandedSlug] = useState("");
  const [variantBusy, setVariantBusy] = useState("");
  const [copiedKey, setCopiedKey] = useState("");
  const [trackingUrls, setTrackingUrls] = useState({}); // slug → [{variant_id, url, angle}]
  const [losers, setLosers] = useState(null); // V202 — preview of weekly killswitch
  const [losersLoading, setLosersLoading] = useState(false);

  const token = () => localStorage.getItem("admin_token") || "";

  const loadTrackingUrls = async (slug) => {
    if (trackingUrls[slug] || !token()) return;
    try {
      const r = await axios.get(`${API}/mika-ads/tracking-urls/${slug}`,
        { headers: { "X-Admin-Token": token() } });
      setTrackingUrls((prev) => ({ ...prev, [slug]: r.data.urls || [] }));
    } catch { /* silent — UI still works without tracking URLs */ }
  };

  const loadLoserPreview = async () => {
    if (!token()) return;
    setLosersLoading(true);
    try {
      const r = await axios.get(`${API}/mika-ads/loser-alerts/preview`,
        { headers: { "X-Admin-Token": token() } });
      setLosers(r.data);
    } catch { /* silent */ }
    finally { setLosersLoading(false); }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 1500);
    });
  };

  const generateVariants = async (slug) => {
    if (!token()) return;
    setVariantBusy(slug); setErr("");
    try {
      await axios.post(`${API}/mika-ads/copy-variants/generate?slug=${slug}`, {},
        { headers: { "X-Admin-Token": token() }, timeout: 60000 });
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "variants_gen_failed");
    } finally { setVariantBusy(""); }
  };

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const r = await axios.get(`${API}/mika-ads/list`);
      setAds(r.data.ads || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "load_failed");
    } finally { setLoading(false); }
  };

  const triggerGenerate = async (only) => {
    if (!token()) { setErr("Admin token chýba."); return; }
    if (!confirm(only
      ? `Re-generovať len '${only}'? Stojí 1 image generation kredit.`
      : "Generovať VŠETKÝCH reklám? Trvá ~3-10 minút a stojí 1 kredit za každý ad slug.")) return;
    setGenerating(true); setGenStatus("");
    try {
      const r = await axios.post(
        `${API}/mika-ads/generate${only ? `?only=${only}` : ""}`, {},
        { headers: { "X-Admin-Token": token() } });
      setGenStatus(`Queued · ${r.data.only}. Refresh za 1-2 min pre nové obrázky.`);
      setTimeout(() => load(), 30000);
    } catch (e) {
      setErr(e.response?.data?.detail || "gen_failed");
    } finally { setGenerating(false); }
  };

  // V219 — Seed presets without generating images (fast, free)
  const seedPresets = async () => {
    if (!token()) { setErr("Admin token chýba."); return; }
    if (!confirm("Seedovať všetky ad presety (vrátane V219 Competitor Killer Pack) do DB? Bez generácie obrázkov — ZADARMO. Reklamy s pre-written copy hneď naskočia do galérie.")) return;
    setGenerating(true); setGenStatus("");
    try {
      const r = await axios.post(`${API}/mika-ads/seed-presets`, {},
        { headers: { "X-Admin-Token": token() } });
      setGenStatus(`✓ Seeded ${r.data.seeded_without_img} nových presetov + ${r.data.with_existing_img} s existujúcim obrázkom.`);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "seed_failed");
    } finally { setGenerating(false); }
  };

  const download = (ad) => {
    const a = document.createElement("a");
    a.href = ad.image_url;
    a.download = `${ad.slug}.png`;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  useEffect(() => { loadLoserPreview(); load(); }, []);

  return (
    <div className="p-4 space-y-5" data-testid="mika-ads-tab">
      <header>
        <h2 className="text-xl font-bold flex items-center gap-2">
          🎭 Mika Ad Hero Gallery
          <span className="text-[10px] tracking-widest bg-pink-500 text-black px-2 py-0.5 font-black uppercase">V199</span>
        </h2>
        <p className="text-sm text-zinc-400 mt-1">
          AI-generované photo-realistické reklamné kreatívy s Mika personou. 6 patternov detected v reálnych vysokovýkonných FB kampaniach.
          1-klik download → upload do Meta Ads Manager.
        </p>
      </header>

      {err && (
        <div className="bg-rose-950/40 border border-rose-700 text-rose-300 p-3 text-sm" data-testid="mika-ads-error">
          {err}
        </div>
      )}

      {/* Generate controls */}
      <section className="border-2 border-pink-700 bg-gradient-to-br from-pink-950/30 to-black p-4 flex items-center gap-3 flex-wrap"
               data-testid="mika-ads-controls">
        <div className="flex-1 min-w-[260px]">
          <div className="text-[10px] uppercase tracking-widest text-pink-400 font-black">⚡ Generácia & Seed</div>
          <div className="text-xs text-zinc-400 mt-1">
            <strong className="text-emerald-300">SEED</strong> = registruje preset s pre-written copy zadarmo (žiadny obrázok).
            <br /><strong className="text-pink-300">GENERATE</strong> = volá AI image gen (1 kredit za slug). Skipne každý slug ktorý už má 50 KB+ PNG.
          </div>
        </div>
        <button onClick={seedPresets}
                disabled={generating}
                data-testid="mika-ads-seed-presets"
                className="text-sm px-5 py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-black uppercase tracking-wider disabled:opacity-50">
          {generating ? "Pracujem…" : "🌱 Seed presety (ZADARMO)"}
        </button>
        <button onClick={() => triggerGenerate(null)}
                disabled={generating}
                data-testid="mika-ads-generate-all"
                className="text-sm px-5 py-2 bg-pink-500 hover:bg-pink-400 text-black font-black uppercase tracking-wider disabled:opacity-50">
          {generating ? "Spúšťam…" : "🎨 Generovať všetkých"}
        </button>
        <button onClick={load} className="text-xs px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:bg-zinc-800">
          🔄 Refresh
        </button>
      </section>

      {genStatus && (
        <div className="border border-pink-700 bg-pink-950/30 p-3 text-xs text-pink-200" data-testid="mika-ads-gen-status">
          ⏳ {genStatus}
        </div>
      )}

      {/* V202 — Loser alerts preview */}
      {losers && (
        <section className={`border ${losers.loser_count > 0
          ? "border-rose-700 bg-rose-950/20"
          : "border-emerald-800 bg-emerald-950/10"} p-4`}
          data-testid="loser-alerts-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-black"
                   style={{ color: losers.loser_count > 0 ? "#FCA5A5" : "#6EE7B7" }}>
                📉 Weekly Killswitch · V202 · piatok 10:00 UTC
              </div>
              <div className="text-sm text-zinc-200 mt-1">
                {losers.loser_count > 0 ? (
                  <>⚠️ <strong className="text-rose-300">{losers.loser_count} loser variantov</strong> má {"<"} {losers.config.loser_share_pct}% click share — kandidáti na vypnutie.</>
                ) : (
                  <>✓ Žiadne loser varianty. Všetky varianty &gt; {losers.config.loser_share_pct}% share (alebo nedostatočná vzorka pre slug).</>
                )}
                <span className="text-zinc-500 ml-2 text-xs">
                  · window {losers.config.window_days}d · min sample {losers.config.min_clicks_per_slug}
                </span>
              </div>
            </div>
            <button onClick={loadLoserPreview}
                    disabled={losersLoading}
                    data-testid="loser-alerts-refresh"
                    className="text-xs px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-50">
              {losersLoading ? "Načítavam…" : "🔄 Refresh"}
            </button>
          </div>
          {losers.loser_count > 0 && (
            <table className="w-full text-xs mt-3 border-t border-zinc-800" data-testid="loser-alerts-table">
              <thead className="text-zinc-500 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="text-left p-2">Slug · Angle</th>
                  <th className="text-right p-2">Clickov</th>
                  <th className="text-right p-2">Spolu</th>
                  <th className="text-right p-2">Share</th>
                </tr>
              </thead>
              <tbody>
                {losers.losers.map((l) => (
                  <tr key={`${l.slug}-${l.variant_id}`}
                      className="border-t border-zinc-900"
                      data-testid={`loser-row-${l.slug}-${l.variant_id}`}>
                    <td className="p-2">
                      <div className="font-mono text-purple-300 text-[10px]">{l.slug}</div>
                      <div className="font-bold text-zinc-200">{l.angle}</div>
                    </td>
                    <td className="p-2 text-right text-rose-400 font-black">{l.clicks}</td>
                    <td className="p-2 text-right text-zinc-500">z {l.slug_total}</td>
                    <td className="p-2 text-right text-rose-300 font-black">{l.share_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {loading && <div className="text-zinc-500 text-sm">Načítavam galériu…</div>}

      {/* Gallery grid */}
      {!loading && ads.length === 0 && (
        <div className="border border-zinc-800 bg-zinc-950 p-8 text-center text-zinc-500 text-sm">
          Zatiaľ žiadne vygenerované reklamy. Klikni 🎨 Generovať všetkých 6.
        </div>
      )}

      <section className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mika-ads-gallery">
        {ads.map((ad) => {
          const badge = PATTERN_BADGES[ad.pattern] || { label: ad.pattern, cls: "bg-zinc-700 text-zinc-200" };
          const hasImage = (ad.file_size_bytes || 0) > 1000;
          const langFlag = ({ sk: "🇸🇰", cs: "🇨🇿", en: "🇬🇧", de: "🇩🇪", pl: "🇵🇱", hu: "🇭🇺" })[ad.lang] || "🌍";
          return (
            <article key={ad.slug}
                     className="border border-zinc-800 bg-zinc-950 overflow-hidden hover:border-pink-600 transition-colors group"
                     data-testid={`mika-ad-${ad.slug}`}>
              <div className="relative aspect-square bg-black overflow-hidden">
                {hasImage ? (
                  <img src={ad.image_url} alt={ad.title}
                       loading="lazy"
                       className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-zinc-900 to-black text-zinc-500 p-4 text-center"
                       data-testid={`mika-ad-placeholder-${ad.slug}`}>
                    <div className="text-5xl mb-2">🎨</div>
                    <div className="text-xs uppercase tracking-widest text-zinc-600 font-black">Obrázok ešte negenerovaný</div>
                    <div className="text-[10px] text-zinc-700 mt-2">Klikni 🔄 nižšie pre generáciu</div>
                  </div>
                )}
                <span className={`absolute top-2 left-2 text-[10px] px-2 py-0.5 font-black uppercase tracking-wider ${badge.cls}`}>
                  {badge.label}
                </span>
                <span className="absolute top-2 right-2 text-[10px] px-2 py-0.5 bg-zinc-900/80 text-zinc-300 font-mono flex items-center gap-1">
                  <span>{langFlag}</span>
                  <span>{hasImage ? `${(ad.file_size_bytes / 1024 / 1024).toFixed(1)} MB` : "no img"}</span>
                </span>
              </div>
              <div className="p-3 space-y-2">
                <div className="text-sm font-bold text-zinc-100 leading-tight">{ad.title}</div>
                <div className="text-xs text-zinc-400 line-clamp-2">{ad.headline}</div>
                <div className="flex items-center justify-between gap-2 pt-1">
                  <button onClick={() => download(ad)}
                          disabled={!hasImage}
                          title={hasImage ? "Stiahnuť PNG pre Meta Ads upload" : "Najprv vygeneruj obrázok klikom 🔄"}
                          data-testid={`mika-ad-download-${ad.slug}`}
                          className="flex-1 text-xs px-3 py-1.5 bg-pink-500 hover:bg-pink-400 text-black font-black uppercase tracking-wider disabled:opacity-30 disabled:cursor-not-allowed">
                    ⬇️ PNG
                  </button>
                  <button onClick={() => triggerGenerate(ad.slug)}
                          disabled={generating}
                          title={hasImage ? "Re-generovať tento jeden" : "Generovať obrázok (1 kredit)"}
                          data-testid={`mika-ad-regen-${ad.slug}`}
                          className="text-xs px-2 py-1.5 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-50">
                    {hasImage ? "🔄" : "🎨"}
                  </button>
                </div>

                {/* V200 — AI Copy Variants */}
                <div className="border-t border-zinc-800 pt-2">
                  {(ad.ai_copy_variants?.length ?? 0) === 0 ? (
                    <button onClick={() => generateVariants(ad.slug)}
                            disabled={variantBusy === ad.slug}
                            data-testid={`mika-ad-gen-variants-${ad.slug}`}
                            className="w-full text-[10px] px-2 py-1.5 border border-purple-700 bg-purple-950/30 text-purple-200 hover:bg-purple-950/50 font-black uppercase tracking-wider disabled:opacity-50">
                      {variantBusy === ad.slug ? "Píšem copy… (~15s)" : "✨ Generovať 4 copy varianty"}
                    </button>
                  ) : (
                    <>
                      <button onClick={() => {
                                const next = expandedSlug === ad.slug ? "" : ad.slug;
                                setExpandedSlug(next);
                                if (next) loadTrackingUrls(next);
                              }}
                              data-testid={`mika-ad-toggle-variants-${ad.slug}`}
                              className="w-full text-[10px] px-2 py-1.5 bg-purple-950/40 border border-purple-700 text-purple-200 hover:bg-purple-950/60 font-black uppercase tracking-wider flex items-center justify-between">
                        <span>✨ {ad.ai_copy_variants.length} copy variantov</span>
                        <span>{expandedSlug === ad.slug ? "▲" : "▼"}</span>
                      </button>
                      {expandedSlug === ad.slug && (
                        <div className="mt-2 space-y-2"
                             data-testid={`mika-ad-variants-${ad.slug}`}>
                          {ad.ai_copy_variants.map((v) => {
                            const tu = (trackingUrls[ad.slug] || []).find((t) => t.variant_id === v.variant_id);
                            return (
                            <div key={v.variant_id}
                                 className="border border-zinc-800 bg-black p-2 text-[11px]"
                                 data-testid={`variant-${ad.slug}-${v.variant_id}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[9px] px-1.5 py-0.5 bg-purple-700 text-white font-black uppercase tracking-wider">
                                  {v.angle}
                                </span>
                                <button onClick={() => {
                                  const blob = `${v.headline}\n\n${v.primary_text}\n\nCTA: ${v.cta}`;
                                  copyToClipboard(blob, `${ad.slug}-${v.variant_id}-all`);
                                }}
                                  data-testid={`copy-all-${ad.slug}-${v.variant_id}`}
                                  className="text-[9px] text-purple-300 hover:text-purple-200 font-black uppercase">
                                  {copiedKey === `${ad.slug}-${v.variant_id}-all` ? "✓ skopírované" : "📋 kopírovať všetko"}
                                </button>
                              </div>
                              <div className="space-y-1">
                                <div className="cursor-pointer hover:bg-zinc-950 p-1 -m-1"
                                     onClick={() => copyToClipboard(v.headline, `${ad.slug}-${v.variant_id}-h`)}
                                     title="Klikni pre kopírovanie headline">
                                  <div className="text-[8px] text-zinc-500 uppercase tracking-widest">Headline</div>
                                  <div className="text-zinc-100 font-bold">{v.headline}
                                    {copiedKey === `${ad.slug}-${v.variant_id}-h` && <span className="ml-1 text-emerald-400">✓</span>}
                                  </div>
                                </div>
                                <div className="cursor-pointer hover:bg-zinc-950 p-1 -m-1"
                                     onClick={() => copyToClipboard(v.primary_text, `${ad.slug}-${v.variant_id}-p`)}
                                     title="Klikni pre kopírovanie primary text">
                                  <div className="text-[8px] text-zinc-500 uppercase tracking-widest">Primary text</div>
                                  <div className="text-zinc-300">{v.primary_text}
                                    {copiedKey === `${ad.slug}-${v.variant_id}-p` && <span className="ml-1 text-emerald-400">✓</span>}
                                  </div>
                                </div>
                                <div className="cursor-pointer hover:bg-zinc-950 p-1 -m-1"
                                     onClick={() => copyToClipboard(v.cta, `${ad.slug}-${v.variant_id}-c`)}
                                     title="Klikni pre kopírovanie CTA">
                                  <div className="text-[8px] text-zinc-500 uppercase tracking-widest">CTA</div>
                                  <div className="text-purple-300 font-bold">{v.cta}
                                    {copiedKey === `${ad.slug}-${v.variant_id}-c` && <span className="ml-1 text-emerald-400">✓</span>}
                                  </div>
                                </div>
                                {/* V201 — Tracking URL for Meta Ads */}
                                {tu && (
                                  <div className="cursor-pointer hover:bg-emerald-950/30 p-1 -m-1 border-t border-zinc-800 pt-1.5 mt-1.5"
                                       onClick={() => copyToClipboard(tu.url, `${ad.slug}-${v.variant_id}-t`)}
                                       title="Klikni pre kopírovanie tracking URL (UTM-tagged)"
                                       data-testid={`tracking-url-${ad.slug}-${v.variant_id}`}>
                                    <div className="text-[8px] text-emerald-500 uppercase tracking-widest">📡 Tracking URL (Meta Ads)</div>
                                    <div className="text-emerald-300 font-mono break-all text-[9px]">{tu.url}
                                      {copiedKey === `${ad.slug}-${v.variant_id}-t` && <span className="ml-1 text-emerald-400">✓</span>}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            );
                          })}
                          <button onClick={() => generateVariants(ad.slug)}
                                  disabled={variantBusy === ad.slug}
                                  className="w-full text-[9px] px-2 py-1 border border-zinc-800 text-zinc-500 hover:text-purple-300 hover:border-purple-700 disabled:opacity-50">
                            {variantBusy === ad.slug ? "Re-generujem…" : "🔄 Re-generovať varianty"}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </section>

      <section className="border border-zinc-800 bg-zinc-950 p-4 text-xs text-zinc-400 leading-relaxed">
        <h3 className="text-zinc-500 uppercase tracking-widest font-black mb-2">ℹ️ Workflow</h3>
        <ol className="list-decimal list-inside space-y-1">
          <li><strong className="text-emerald-300">🌱 Seed presety</strong> — 1× klikni po deploy aby sa všetky presety (vrátane V219 Competitor Killer Pack) zaregistrovali s pre-written FB copy. ZADARMO, žiadne kredity.</li>
          <li>Klikni <strong className="text-pink-300">🎨</strong> alebo <strong className="text-pink-300">🔄</strong> pri reklame, ktorú chceš vygenerovať ako obrázok (1 kredit za slug).</li>
          <li>Klikni <strong className="text-purple-300">✨ {`{N}`} copy variantov</strong> pri karte → klikni na pole {`Primary text`}, {`Headline`} alebo {`CTA`} pre kopírovanie do schránky.</li>
          <li>Klikni <strong className="text-pink-300">⬇️ PNG</strong> pri reklame ktorá ti pasuje, otvor Meta Ads Manager → Vytvor reklamu → Upload tento PNG ako media + paste primary_text + headline + CTA.</li>
        </ol>
        <h3 className="text-zinc-500 uppercase tracking-widest font-black mt-4 mb-2">🎯 Patterny</h3>
        <ul className="list-disc list-inside space-y-1">
          <li><strong className="text-yellow-400">Tripwire €7</strong> — Billy Darr Media pattern, low-ticket entry offer</li>
          <li><strong className="text-amber-400">STOP banner</strong> — AG marketing slovak pattern, {`ZADARMO`} promise</li>
          <li><strong className="text-cyan-400">90s Timer</strong> — Inspeccia pattern, neon glow + Mika authority</li>
          <li><strong className="text-emerald-400">Big Money</strong> — trycrush.ai pattern, {`FOUNDER GENERATES €X`}</li>
          <li><strong className="text-blue-400">Coming Soon Mika</strong> — MojaRezervácia pattern + Mika holding tablet</li>
          <li><strong className="text-rose-400">Competitor Killer Mika</strong> — Lead Flow pattern + Mika personal authority</li>
          <li><strong className="text-fuchsia-400">Before/After (TryCrush-killer)</strong> — V218.5 pattern, metric card transformation</li>
          <li><strong className="text-emerald-300">⚡ Pain-First (V219)</strong> — Agents Pro Pack $19 killer, {`Pouzivas AI manualne?`} → SK/CZ/EN</li>
          <li><strong className="text-yellow-300">💰 Value Stack (V219)</strong> — Skills AI Pack $97 killer, €4 200 hodnota za €49 → SK/CZ/EN</li>
          <li><strong className="text-blue-300">🇪🇺 EU Local (V219)</strong> — Mape Systems SK killer, {`Pracuj NA firme`} → SK/CZ</li>
          <li><strong className="text-indigo-300">🛡️ EU Sovereign (V219)</strong> — anti-US Big Tech play, {`Data stays in EU`} → EN</li>
          <li><strong className="text-violet-300">🛰️ Mission Control (V220)</strong> — RankWay killer, {`Riadim cely biznis`} → SK/CZ/EN</li>
          <li><strong className="text-amber-300">🏝️ Hammock Mode (V220)</strong> — disruptive counter, {`Kym spis, 20 agentov pracuje`} → SK/CZ/EN</li>
          <li><strong className="text-rose-300">🎯 4 Verticals (V220)</strong> — SK personas grid, Pekáreň + Advokátka + E-shop + Stavbár → SK/CZ/EN</li>
          <li><strong className="text-red-300">🔥 Competitor Envy (V223)</strong> — FOMO funnel, {`Tvoja konkurencia to uz ma`} → SK/CZ/EN</li>
          <li><strong className="text-violet-300">🚂 Last Train (V223)</strong> — historical wave parallel, {`Posledny vlak: nastupis?`} → SK/CZ/EN</li>
          <li><strong className="text-cyan-300">📅 Year 2026 (V223)</strong> — editorial scarcity, {`Bez AI agentov neprerazis`} → SK/CZ/EN</li>
          <li><strong className="text-yellow-300">⚖️ EU AI Act D-47 (V223)</strong> — regulatory countdown, {`47 dni do pokuty 35M EUR`} → SK/CZ/EN</li>
        </ul>
      </section>
    </div>
  );
}
